/**
 * The only redaction implementation. `@/lib/logger` calls this internally, so an unredacted payload
 * cannot be logged even by a caller who has not read the rules.
 */

const REDACTED = '[REDACTED]';

/**
 * Account details are redacted even though the player can see them on screen. A rendered page and a
 * log line are different exposure surfaces: logs get pasted into support tickets, screenshots, and
 * group chats.
 */
const SENSITIVE_KEYS: ReadonlySet<string> = new Set([
  'token',
  'utr',
  'payerwalletnumber',
  'accountnumber',
  'accountholdername',
  'providerreference',
  'authorization',
  'secret',
  'password',
  'otp',
  'pin',
  'email',
  'phone',
  'playerref',
]);

/** The session token is `{live|sandbox}.{opaque}`, which makes it recognisable inside any string. */
const TOKEN_IN_STRING = /\b(live|sandbox)\.[A-Za-z0-9_-]{8,}/g;

const MAX_DEPTH = 6;

function redactString(value: string): string {
  return value.replace(TOKEN_IN_STRING, `$1.${REDACTED}`);
}

/**
 * Deep-copies with sensitive values replaced. Never mutates the input — a logger that altered the
 * object it was handed would corrupt application state from a debug statement.
 */
export function redact(value: unknown, depth = 0, seen = new WeakSet()): unknown {
  if (typeof value === 'string') return redactString(value);
  if (value === null || typeof value !== 'object') return value;
  if (depth >= MAX_DEPTH) return '[Object]';
  if (seen.has(value)) return '[Circular]';
  seen.add(value);

  if (value instanceof Error) {
    return { name: value.name, message: redactString(value.message) };
  }

  if (typeof File !== 'undefined' && value instanceof File) {
    return `[File name=${redactString(value.name)} size=${String(value.size)}]`;
  }

  if (typeof Blob !== 'undefined' && value instanceof Blob) {
    return `[Blob size=${String(value.size)} type=${value.type}]`;
  }

  if (typeof FormData !== 'undefined' && value instanceof FormData) {
    const keys: string[] = [];
    value.forEach((_entry, key) => {
      keys.push(key);
    });
    return `[FormData keys=${keys.join(',')}]`;
  }

  if (typeof Headers !== 'undefined' && value instanceof Headers) {
    const output: Record<string, unknown> = {};
    value.forEach((headerValue, key) => {
      output[key] = SENSITIVE_KEYS.has(key.toLowerCase()) ? REDACTED : redactString(headerValue);
    });
    return output;
  }

  if (Array.isArray(value)) {
    return value.map((item) => redact(item, depth + 1, seen));
  }

  const output: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value)) {
    output[key] = SENSITIVE_KEYS.has(key.toLowerCase()) ? REDACTED : redact(item, depth + 1, seen);
  }
  return output;
}
